import {
  Button, Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText, FormLabel,
  Input,
  InputLabel,
  makeStyles,
  Slide,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import {TransitionProps} from '@material-ui/core/transitions/transition';
import React, {useEffect, useRef, useState} from 'react';
import * as yup from 'yup';
import {Formik, FormikProps} from 'formik';
import Loading from "../../UiComponents/Loading";
import {TrafficShapingSettings} from "../../../Domain/Models/TrafficShapingSettings";
import {TrafficShapingService} from "../../../Domain/Services/TrafficShapingService";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
  form: {
    '& > *': {
      margin: theme.spacing(1),
    },
  },
  dialog: {
    alignItems: 'flex-start',
  },
  deleteFlex: {
    flex: 1,
  },
  formLabel: {
    marginTop: theme.spacing(4),
  }
}));

export interface SettingsModalProps
{
  open: boolean;
  isRunning: boolean;
  onClose: () => void;
}

export default function SettingsModal(props: SettingsModalProps)
{
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const {open, isRunning, onClose} = props;
  const classes = useStyles();

  const [settings, setSettings] = useState<TrafficShapingSettings>();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();

  const formRef = useRef<FormikProps<TrafficShapingSettings>>(null);

  const schema = yup.object().shape({
    queueTimeoutLimit: yup.number().positive('Must be greater than zero.'),
  });

  const handleSubmit = (value: TrafficShapingSettings) =>
  {
    setSubmitting(true);
    setSubmitError(undefined);

    TrafficShapingService.updateSettings(value)
      .then(() => onClose())
      .catch((e: Error) =>
      {
        setSubmitting(false);
        setSubmitError(e.message);
      });
  }

  useEffect(() =>
  {
    if (open)
    {
      setSubmitError(undefined);
      setSubmitting(true);

      TrafficShapingService.getSettings()
        .then((result) => setSettings(result))
        .then(() => setSubmitting(false))
        .catch((e: Error) =>
        {
          setSubmitting(false);
          setSubmitError(e.message);
        });
    }
    else
    {
      setSettings(undefined);
    }
  }, [open]);

  return (
    <Dialog
      classes={{scrollPaper: classes.dialog}}
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      disableEscapeKeyDown
      disableBackdropClick={submitting}
      TransitionComponent={Transition}
    >
      <DialogTitle>Settings</DialogTitle>

      <Loading isLoading={submitting}>
        <DialogContent>
          {settings !== undefined && (
            <Formik<TrafficShapingSettings>
              initialValues={settings}
              onSubmit={(value) => handleSubmit(value)}
              validationSchema={schema}
              innerRef={formRef}
            >
              {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur
                }) => (
                <>
                  <FormControl fullWidth margin="normal"
                               error={touched.numberOfCubesToComplete && errors.numberOfCubesToComplete !== undefined}>
                    <InputLabel htmlFor="component-error">Number Of Cubes To Complete</InputLabel>
                    <Input
                      name="numberOfCubesToComplete"
                      type="number"
                      value={values.numberOfCubesToComplete}
                      readOnly
                      onChange={handleChange}
                      onBlur={handleBlur}/>
                    <FormHelperText>{touched.numberOfCubesToComplete && errors.numberOfCubesToComplete}</FormHelperText>
                  </FormControl>

                  <FormControl fullWidth margin="normal"
                               error={touched.queueTimeoutDuration && errors.queueTimeoutDuration !== undefined}>
                    <InputLabel htmlFor="component-error">Queue Timeout Duration</InputLabel>
                    <Input
                      name="queueTimeoutDuration"
                      value={values.queueTimeoutDuration}
                      onChange={handleChange}
                      onBlur={handleBlur}/>
                    <FormHelperText>{touched.queueTimeoutDuration && errors.queueTimeoutDuration}</FormHelperText>
                  </FormControl>

                  <FormControl fullWidth margin="normal"
                               error={touched.cubeAssignmentLimit && errors.cubeAssignmentLimit !== undefined}>
                    <InputLabel htmlFor="component-error">Cube Assignment Limit</InputLabel>
                    <Input
                      name="cubeAssignmentLimit"
                      value={values.cubeAssignmentLimit}
                      onChange={handleChange}
                      onBlur={handleBlur}/>
                    <FormHelperText>{touched.cubeAssignmentLimit && errors.cubeAssignmentLimit}</FormHelperText>
                  </FormControl>



                  <div className={classes.formLabel}/>
                  <FormLabel>RULES</FormLabel>
                  <div className={classes.formLabel}/>
                  
                  <FormLabel>Bullet Time Rule</FormLabel>
                  <FormControl fullWidth margin="none"
                               error={touched.bulletTimeRule?.validateRule && errors.bulletTimeRule?.validateRule !== undefined}>
                    <InputLabel htmlFor="component-error">Enable Rule</InputLabel>
                    <Checkbox
                      name="bulletTimeRule.validateRule"
                      checked={values.bulletTimeRule?.validateRule}
                      onChange={handleChange}
                      color="primary"
                    />
                    <FormHelperText>{touched.bulletTimeRule?.validateRule && errors.bulletTimeRule?.validateRule}</FormHelperText>
                  </FormControl>


                  <div className={classes.formLabel}/>
                  <FormControl fullWidth margin="none">
                    <InputLabel htmlFor="component-error">Disable All Rules</InputLabel>
                    <Checkbox
                      name="disableAllRules"
                      checked={values.disableAllRules}
                      onChange={handleChange}
                      color="primary"
                    />
                    <FormHelperText error>
                      {!values.disableAllRules && (
                      'This setting is overridden if any cube is in Maintenance Mode or if an error occured during tree calculation.')}
                    </FormHelperText>
                  </FormControl>

                  {!values.disableAllRules && (
                    <>
                      <FormControl fullWidth margin="normal"
                                   error={touched.numberOfStartPhaseCubes && errors.numberOfStartPhaseCubes !== undefined}>
                        <InputLabel htmlFor="component-error">Number Of Start Phase Cubes</InputLabel>
                        <Input
                          name="numberOfStartPhaseCubes"
                          type="number"
                          value={values.numberOfStartPhaseCubes}
                          onChange={handleChange}
                          onBlur={handleBlur}/>
                        <FormHelperText>{touched.numberOfStartPhaseCubes && errors.numberOfStartPhaseCubes}</FormHelperText>
                      </FormControl>

                      <FormControl fullWidth margin="normal"
                                   error={touched.numberOfEndPhaseCubes && errors.numberOfEndPhaseCubes !== undefined}>
                        <InputLabel htmlFor="component-error">Number Of End Phase Cubes</InputLabel>
                        <Input
                          name="numberOfEndPhaseCubes"
                          type="number"
                          value={values.numberOfEndPhaseCubes}
                          onChange={handleChange}
                          onBlur={handleBlur}/>
                        <FormHelperText>{touched.numberOfEndPhaseCubes && errors.numberOfEndPhaseCubes}</FormHelperText>
                      </FormControl>


                      <div className={classes.formLabel}/>
                      <FormLabel>Number Of Players Rule</FormLabel>
                      <FormControl fullWidth margin="none"
                                   error={touched.numberOfPlayersRule?.validateRule && errors.numberOfPlayersRule?.validateRule !== undefined}>
                        <InputLabel htmlFor="component-error">Enable Rule</InputLabel>
                        <Checkbox
                          name="numberOfPlayersRule.validateRule"
                          checked={values.numberOfPlayersRule?.validateRule}
                          onChange={handleChange}
                          color="primary"
                        />
                        <FormHelperText>{touched.numberOfPlayersRule?.validateRule && errors.numberOfPlayersRule?.validateRule}</FormHelperText>
                      </FormControl>

                      {values.numberOfPlayersRule.validateRule && (
                        <>
                          <FormControl fullWidth margin="normal"
                                       error={touched.numberOfPlayersRule?.minimum1PlayerGames && errors.numberOfPlayersRule?.minimum1PlayerGames !== undefined}>
                            <InputLabel htmlFor="component-error">Minimum Number of 1 Player Games</InputLabel>
                            <Input
                              name="numberOfPlayersRule.minimum1PlayerGames"
                              type="number"
                              value={values.numberOfPlayersRule?.minimum1PlayerGames}
                              onChange={handleChange}
                              onBlur={handleBlur}/>
                            <FormHelperText>{touched.numberOfPlayersRule?.minimum1PlayerGames && errors.numberOfPlayersRule?.minimum1PlayerGames}</FormHelperText>
                          </FormControl>

                          <FormControl fullWidth margin="normal"
                                       error={touched.numberOfPlayersRule?.minimum2PlayerGames && errors.numberOfPlayersRule?.minimum2PlayerGames !== undefined}>
                            <InputLabel htmlFor="component-error">Minimum Number of 2 Player Games</InputLabel>
                            <Input
                              name="numberOfPlayersRule.minimum2PlayerGames"
                              type="number"
                              value={values.numberOfPlayersRule?.minimum2PlayerGames}
                              onChange={handleChange}
                              onBlur={handleBlur}/>
                            <FormHelperText>{touched.numberOfPlayersRule?.minimum2PlayerGames && errors.numberOfPlayersRule?.minimum2PlayerGames}</FormHelperText>
                          </FormControl>
                        </>
                      )}


                      <div className={classes.formLabel}/>
                      <FormLabel>Game Type Rule</FormLabel>
                      <FormControl fullWidth margin="none"
                                   error={touched.gameTypeRule?.validateRule && errors.gameTypeRule?.validateRule !== undefined}>
                        <InputLabel htmlFor="component-error">Enable Rule</InputLabel>
                        <Checkbox
                          name="gameTypeRule.validateRule"
                          checked={values.gameTypeRule?.validateRule}
                          onChange={handleChange}
                          color="primary"
                        />
                        <FormHelperText>{touched.gameTypeRule?.validateRule && errors.gameTypeRule?.validateRule}</FormHelperText>
                      </FormControl>

                      {values.gameTypeRule.validateRule && (
                        <>
                          <FormControl fullWidth margin="normal"
                                       error={touched.gameTypeRule?.minimumTypesToPlay && errors.gameTypeRule?.minimumTypesToPlay !== undefined}>
                            <InputLabel htmlFor="component-error">Minimum Number of All Types To Play</InputLabel>
                            <Input
                              name="gameTypeRule.minimumTypesToPlay"
                              type="number"
                              value={values.gameTypeRule?.minimumTypesToPlay}
                              onChange={handleChange}
                              onBlur={handleBlur}/>
                            <FormHelperText>{touched.gameTypeRule?.minimumTypesToPlay && errors.gameTypeRule?.minimumTypesToPlay}</FormHelperText>
                          </FormControl>

                          <FormControl fullWidth margin="normal"
                                       error={touched.gameTypeRule?.maximumRepeat && errors.gameTypeRule?.maximumRepeat !== undefined}>
                            <InputLabel htmlFor="component-error">Maximum Number Of Times A Game Type Can Be
                              Repeaed</InputLabel>
                            <Input
                              name="gameTypeRule.maximumRepeat"
                              type="number"
                              value={values.gameTypeRule?.maximumRepeat}
                              onChange={handleChange}
                              onBlur={handleBlur}/>
                            <FormHelperText>{touched.gameTypeRule?.maximumRepeat && errors.gameTypeRule?.maximumRepeat}</FormHelperText>
                          </FormControl>
                        </>
                      )}


                      <div className={classes.formLabel}/>
                      <FormLabel>Difficulty Rule</FormLabel>
                      <FormControl fullWidth margin="none"
                                   error={touched.difficultyRule?.validateRule && errors.difficultyRule?.validateRule !== undefined}>
                        <InputLabel htmlFor="component-error">Enable Rule</InputLabel>
                        <Checkbox
                          name="difficultyRule.validateRule"
                          checked={values.difficultyRule?.validateRule}
                          onChange={handleChange}
                          color="primary"
                        />
                        <FormHelperText>{touched.difficultyRule?.validateRule && errors.difficultyRule?.validateRule}</FormHelperText>
                      </FormControl>


                      <div className={classes.formLabel}/>
                      <FormLabel>Length Of Game Rule</FormLabel>
                      <FormControl fullWidth margin="none"
                                   error={touched.lengthOfGameRule?.validateRule && errors.lengthOfGameRule?.validateRule !== undefined}>
                        <InputLabel htmlFor="component-error">Enable Rule</InputLabel>
                        <Checkbox
                          name="lengthOfGameRule.validateRule"
                          checked={values.lengthOfGameRule?.validateRule}
                          onChange={handleChange}
                          color="primary"
                        />
                        <FormHelperText>{touched.lengthOfGameRule?.validateRule && errors.lengthOfGameRule?.validateRule}</FormHelperText>
                      </FormControl>

                      {values.lengthOfGameRule.validateRule && (
                        <>
                          <FormControl fullWidth margin="normal"
                                       error={touched.lengthOfGameRule?.maximumQuickGames && errors.lengthOfGameRule?.maximumQuickGames !== undefined}>
                            <InputLabel htmlFor="component-error">Maximum Number Of Quick Games</InputLabel>
                            <Input
                              name="lengthOfGameRule.maximumQuickGames"
                              type="number"
                              value={values.lengthOfGameRule?.maximumQuickGames}
                              onChange={handleChange}
                              onBlur={handleBlur}/>
                            <FormHelperText>{touched.lengthOfGameRule?.maximumQuickGames && errors.lengthOfGameRule?.maximumQuickGames}</FormHelperText>
                          </FormControl>
                        </>
                      )}


                      <div className={classes.formLabel}/>
                      <FormLabel>Forced Break Rule</FormLabel>
                      <FormControl fullWidth margin="none"
                                   error={touched.forcedBreakRule?.validateRule && errors.forcedBreakRule?.validateRule !== undefined}>
                        <InputLabel htmlFor="component-error">Enable Rule</InputLabel>
                        <Checkbox
                          name="forcedBreakRule.validateRule"
                          checked={values.forcedBreakRule?.validateRule}
                          onChange={handleChange}
                          color="primary"
                        />
                        <FormHelperText>{touched.forcedBreakRule?.validateRule && errors.forcedBreakRule?.validateRule}</FormHelperText>
                      </FormControl>

                      {values.forcedBreakRule.validateRule && (
                        <>
                          <FormControl fullWidth margin="normal"
                                       error={touched.forcedBreakRule?.afterCubeNumber && errors.forcedBreakRule?.afterCubeNumber !== undefined}>
                            <InputLabel htmlFor="component-error">Break After Game Number</InputLabel>
                            <Input
                              name="forcedBreakRule.afterCubeNumber"
                              type="number"
                              value={values.forcedBreakRule?.afterCubeNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}/>
                            <FormHelperText>{touched.forcedBreakRule?.afterCubeNumber && errors.forcedBreakRule?.afterCubeNumber}</FormHelperText>
                          </FormControl>

                          <FormControl fullWidth margin="normal"
                                       error={touched.forcedBreakRule?.breakTime && errors.forcedBreakRule?.breakTime !== undefined}>
                            <InputLabel htmlFor="component-error">Break Length</InputLabel>
                            <Input
                              name="forcedBreakRule.breakTime"
                              value={values.forcedBreakRule?.breakTime}
                              onChange={handleChange}
                              onBlur={handleBlur}/>
                            <FormHelperText>{touched.forcedBreakRule?.breakTime && errors.forcedBreakRule?.breakTime}</FormHelperText>
                          </FormControl>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </Formik>
          )}

          {submitError != undefined && (
            <Typography component="h3" variant="subtitle1" color="error" gutterBottom>
              {submitError}
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={onClose} color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={() => formRef.current?.handleSubmit()}
            disabled={isRunning}
            color="primary"
          >
            Submit
          </Button>
        </DialogActions>

      </Loading>
    </Dialog>
  );
}